import type { GlobalSearchSuggestion } from './types.ts';
import { useQuery } from '@tanstack/react-query';
import { linkOptions } from '@tanstack/react-router';
import clsx from 'clsx';
import { Duration } from 'luxon';
import { useMemo } from 'react';
import { AiOutlineTags } from 'react-icons/ai';
import { PiMagnifyingGlassBold } from 'react-icons/pi';
import { FormattedMessage } from 'react-intl';
import { useTailwindBreakpoint } from '@_/hooks';
import { organizationCommonI18n } from '@_/i18n-common';
import { AutocompleteList, AutocompleteListItem, Avatar, Bleed, Button, Link, Text } from '@_/ui';
import { getReactQueryOptions } from '@_/utils-frontend';
import { publicApiQueryClient } from '#src/api.ts';
import { headingI18n } from '#src/i18nMessages/index.ts';
import { LinkOptionsBuilder } from '#src/lib/LinkOptionsBuilder.ts';

export function GlobalSearchOptions({
  items,
  onSelectItem
}: {
  items: GlobalSearchSuggestion[];
  onSelectItem?: () => void;
}) {
  const isDesktop = useTailwindBreakpoint('sm');

  const popularTopicsQuery = useQuery({
    ...getReactQueryOptions(publicApiQueryClient.popularTopics.list),
    staleTime: Duration.fromObject({ minutes: 10 }).toMillis()
  });

  const popularTopics = popularTopicsQuery.data?.data;

  return (
    <div>
      {!!items.length && (
        <AutocompleteList>
          {(item: GlobalSearchSuggestion) => (
            <GlobalSearchOption
              key={item.object === 'inputValue' ? `search-${item.inputValue}` : `${item.id}`}
              item={item}
              onSelectTopic={onSelectItem}
            />
          )}
        </AutocompleteList>
      )}

      {!items.length && (
        <div className="py-3">
          {!!popularTopics && (
            <section>
              <Text className="mb-2.5 sm:mb-3" semibold size="xs" subdued render={<header />}>
                <FormattedMessage {...headingI18n.popularTopics} />
              </Text>
              <div className="-mx-0.5 flex flex-wrap items-center gap-2">
                {popularTopics.map(topic => (
                  <Button
                    key={topic.id}
                    onClick={onSelectItem}
                    renderLink={props => (
                      <Link
                        {...props}
                        from="/{-$locale}"
                        to="/{-$locale}/courses"
                        search={{ topic: topic.slug }}
                      />
                    )}
                    rounded
                    size={isDesktop ? 'sm' : 'xs'}
                    variant="outline"
                  >
                    {topic.name}
                  </Button>
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}

function GlobalSearchOption({
  item,
  onSelectTopic
}: {
  item: GlobalSearchSuggestion;
  onSelectTopic?: () => void;
}) {
  const linkProps = useMemo(() => {
    if (item.object === 'venue') {
      return LinkOptionsBuilder.venue({ venue: item });
    }

    if (item.object === 'topic') {
      return linkOptions({
        from: '/{-$locale}',
        to: '/{-$locale}/courses',
        search: { topic: item.slug }
      });
    }

    return linkOptions({
      from: '/{-$locale}',
      to: '/{-$locale}/venues',
      search: { search: item.inputValue }
    });
  }, [item]);

  let icon: React.ReactNode;
  if (item.object === 'venue') {
    icon = (
      <Avatar
        className="size-full"
        kind="organization"
        name={item.name}
        src={item.organization?.avatarUrl}
      />
    );
  } else {
    const content =
      item.object === 'topic' ? (
        <AiOutlineTags className="size-5 sm:size-4" />
      ) : (
        <PiMagnifyingGlassBold className="size-4 sm:size-3.5" />
      );

    icon = <div className="flex size-full items-center justify-center">{content}</div>;
  }

  let label: React.ReactNode;
  if (item.object === 'venue') {
    label = (
      <>
        <p>{item.name}</p>
        <p className={clsx('hidden text-xs sm:block', 'not-group-data-highlighted:text-gray-500')}>
          {item.isSchool ? (
            <FormattedMessage {...organizationCommonI18n.school} />
          ) : (
            <FormattedMessage {...organizationCommonI18n.business} />
          )}
        </p>
      </>
    );
  } else if (item.object === 'topic') {
    label = item.name;
  } else {
    label = (
      <FormattedMessage
        id="search.query-all-results"
        defaultMessage='See all results for "{value}"'
        values={{ value: item.inputValue }}
      />
    );
  }

  return (
    <AutocompleteListItem
      render={renderProps => (
        <Link {...renderProps} {...linkProps} className="group block" onClick={onSelectTopic} plain>
          <Bleed
            className={clsx(
              'flex items-center gap-x-3 py-2.5 sm:py-3',
              'group-data-highlighted:bg-primary-600 group-data-highlighted:data-highlighted:text-white',
              'not-group-data-highlighted:text-gray-800'
            )}
            horizontal
          >
            <div
              className={clsx(
                'size-6 rounded-full sm:size-8',
                'sm:group-data-highlighted:bg-primary-200 sm:group-data-highlighted:text-primary-600',
                'sm:not-group-data-highlighted:bg-gray-100 sm:not-group-data-highlighted:text-gray-800'
              )}
            >
              {icon}
            </div>

            <div className="group-data-highlighted:text-white sm:font-medium">{label}</div>
          </Bleed>
        </Link>
      )}
      value={item}
    />
  );
}
